import Header from '../components/Header';
import { motion } from 'framer-motion';
import {
  Sparkles, Users, Plane, Hotel, Globe, ShieldCheck, HeartHandshake,
  Phone, Mail, MessageCircle, MapPin, Star, Compass, BookOpen
} from 'lucide-react';

const tourPackages = [
  { name: 'Cambodia', tagline: 'Temples & Heritage' },
  { name: 'Indonesia', tagline: 'Islands & Culture' },
  { name: 'India', tagline: 'Spirituality & History' },
  { name: 'Singapore', tagline: 'Modern & Vibrant' },
  { name: 'Malaysia', tagline: 'Diverse & Beautiful' },
  { name: 'Vietnam', tagline: 'Nature & Tradition' },
  { name: 'Thailand', tagline: 'Temples & Beaches' },
  { name: 'Philippines', tagline: 'Islands & Adventure' },
  { name: 'Sri Lanka', tagline: 'Heritage & Serenity' },
  { name: 'Australia', tagline: 'Nature & Adventure' },
];

const services = [
  { icon: Sparkles, title: 'AI-Powered Trip Planning', desc: 'Personalised itineraries with weather insights, cultural tips, packing lists, and real-time budget tracking.' },
  { icon: Users, title: 'Group & Family Travel', desc: 'Solo, couple, family with kids, or business — every trip tailored to your exact needs.' },
  { icon: Plane, title: 'Flights & Hotels', desc: 'Search and book through our AI-enabled system and trusted travel partners.' },
  { icon: Compass, title: 'Curated Tour Packages', desc: 'Handcrafted itineraries to Asia — temples, heritage sites, and authentic cultural experiences.' },
  { icon: Globe, title: 'Travel Services', desc: 'Flight bookings, hotel reservations, airport transfers, car rentals, travel insurance, eSim, and more.' },
  { icon: BookOpen, title: 'Visa & Special Services', desc: 'All visa services plus business, executive, VIP services, and complimentary airport transfers.' },
];

const reasons = [
  { icon: ShieldCheck, text: 'End-to-end service — from start to destination and back. Complete peace of mind.' },
  { icon: HeartHandshake, text: 'Personal touch — reach us directly via WhatsApp or phone, at any time.' },
  { icon: Star, text: 'Discounted flights, hotels, and transportation are assured.' },
  { icon: Plane, text: 'Hassle-free service on cancellations, rescheduling and delays. Quick alternatives arranged.' },
  { icon: MapPin, text: 'Expertise in cultural, historical, heritage, spiritual and temple tourism across Asia.' },
  { icon: Globe, text: 'Multi-currency pricing (USD, AUD, MYR, SGD, INR) for a global traveler base.' },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

export default function AboutPage() {
  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="about-page">
      {/* Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-40 right-16 w-[420px] h-[420px] bg-gold/12 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-32 left-8 w-96 h-96 bg-sky/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto space-y-16">

          {/* ───── Hero ───── */}
          <motion.section initial="hidden" animate="show" variants={stagger} className="text-center" data-testid="about-hero">
            <motion.div variants={fadeUp} className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Globe className="w-8 h-8 text-gold" />
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">
              About Triple A Travel Solutions
            </motion.h1>
            <motion.p variants={fadeUp} className="text-gold font-semibold text-lg sm:text-xl tracking-wide mb-6">
              Awake. Aware. Arise for better travel experiences.
            </motion.p>
            <motion.div variants={fadeUp} className="max-w-3xl mx-auto bg-white/10 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/15 text-white/90 text-sm sm:text-base leading-relaxed space-y-4">
              <p>
                Triple A Travel Solutions is a curated travel company providing end-to-end travel services to all kinds of travel needs — individuals, couples, families, friends, groups, events, and businesses. Once engaged, we work on your behalf and take care of everything for a hassle-free, smooth travel experience, giving you complete peace of mind.
              </p>
              <p>
                We specialise in cultural, spiritual, and heritage tours across Asia. Founded with a passion for meaningful travels and self-actualisation, we design journeys that go beyond sightseeing — connecting travellers with nature, history, culture, spirituality, and the beauty of every destination.
              </p>
              <p className="text-gold/90 font-medium italic">
                Travelling shouldn't be an activity. It should be a wonderful experience. We are here to provide that experience.
              </p>
            </motion.div>
          </motion.section>

          {/* ───── What We Do ───── */}
          <motion.section initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger} data-testid="about-services">
            <motion.h2 variants={fadeUp} className="font-serif text-2xl sm:text-3xl font-bold text-white text-center mb-8">What We Do</motion.h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s, i) => (
                <motion.div key={s.title || `svc-${i}`} variants={fadeUp}
                  className="bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-colors duration-200"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center mb-3">
                    <s.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-1.5">{s.title}</h3>
                  <p className="text-white/65 text-xs leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ───── Why Choose Us ───── */}
          <motion.section initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger} data-testid="about-why-choose">
            <motion.h2 variants={fadeUp} className="font-serif text-2xl sm:text-3xl font-bold text-white text-center mb-3">Why Choose Us</motion.h2>
            <motion.p variants={fadeUp} className="text-white/70 text-center text-sm max-w-2xl mx-auto mb-8">
              There are many travel providers offering excellent service when everything goes in order. We provide the same excellent service even when things are not going in order. That's our strength. Ours is not just a booking service — a complete travel solution.
            </motion.p>
            <div className="grid sm:grid-cols-2 gap-3">
              {reasons.map((r, i) => (
                <motion.div key={r.text?.slice(0, 20) || `reason-${i}`} variants={fadeUp}
                  className="flex items-start gap-3 bg-white/8 backdrop-blur-lg border border-white/10 rounded-xl p-4 hover:bg-white/12 transition-colors"
                  data-testid={`reason-card-${i}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <r.icon className="w-4 h-4 text-gold" />
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">{r.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ───── Tour Packages ───── */}
          <motion.section initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger} data-testid="about-tour-packages">
            <motion.h2 variants={fadeUp} className="font-serif text-2xl sm:text-3xl font-bold text-white text-center mb-8">Our Tour Packages</motion.h2>
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {tourPackages.map((pkg, i) => (
                <motion.div key={pkg.name || `pkg-${i}`} variants={fadeUp}
                  className="bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-4 text-center hover:bg-white/18 hover:scale-[1.03] transition-all duration-200 cursor-default group"
                  data-testid={`tour-package-${pkg.name.toLowerCase()}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-2 group-hover:bg-gold/25 transition-colors">
                    <MapPin className="w-4.5 h-4.5 text-gold" />
                  </div>
                  <p className="font-semibold text-white text-sm">{pkg.name}</p>
                  <p className="text-white/50 text-[11px] mt-0.5">{pkg.tagline}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ───── Get in Touch ───── */}
          <motion.section initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger}
            className="bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-6 sm:p-8" data-testid="about-contact"
          >
            <motion.h2 variants={fadeUp} className="font-serif text-2xl sm:text-3xl font-bold text-white text-center mb-2">Get in Touch</motion.h2>
            <motion.p variants={fadeUp} className="text-white/60 text-center text-sm mb-6">Based in Australia, serving travellers worldwide.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <a href="tel:+61401050457"
                className="flex items-center gap-2 bg-white/8 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 text-white/85 text-sm transition-colors"
                data-testid="about-phone-1"
              >
                <Phone className="w-4 h-4 text-gold" /> +61 401 050 457
              </a>
              <a href="tel:+61424024078"
                className="flex items-center gap-2 bg-white/8 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 text-white/85 text-sm transition-colors"
                data-testid="about-phone-2"
              >
                <Phone className="w-4 h-4 text-gold" /> +61 424 024 078
              </a>
              <a href="mailto:triplea@tripleabooking.com"
                className="flex items-center gap-2 bg-white/8 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 text-white/85 text-sm transition-colors"
                data-testid="about-email"
              >
                <Mail className="w-4 h-4 text-gold" /> triplea@tripleabooking.com
              </a>
              <a href="https://api.whatsapp.com/send?phone=61401050457&text=Hi%20Triple%20A%20Travel%20Solutions" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-600/20 hover:bg-green-600/30 border border-green-500/20 rounded-xl px-5 py-3 text-green-300 text-sm transition-colors"
                data-testid="about-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </motion.div>
          </motion.section>

        </div>
      </main>
    </div>
  );
}
